import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowRight, ChevronDown, Sparkles, Building2, GraduationCap, Brain, Check, Users } from 'lucide-react';
import { heroData, featuresData } from '../data/mock';
import { Cube3D, Pyramid3D, Sphere3D, Hexagon3D, IsometricContainer } from './3DAssets';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectTimeoutRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    // Trigger card animations
    setTimeout(() => setVisibleCards([0, 1, 2, 3]), 300);

    // Reset redirect state and clear any pending timeouts on mount (Fixes back button persistence)
    const resetRedirect = () => {
      setIsRedirecting(false);
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };

    resetRedirect();

    // Handle bfcache (back-forward cache) to ensure overlay clears on back navigation
    const handlePageShow = (event) => {
      resetRedirect();
    };
    window.addEventListener('pageshow', handlePageShow);

    // Track mouse for 3D parallax - Simplified for better performance
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 8,
        y: (e.clientY / window.innerHeight - 0.5) * 8
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('pageshow', handlePageShow);
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);


  const handleAcademyRedirect = () => {
    setIsRedirecting(true);
    if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    redirectTimeoutRef.current = setTimeout(() => {
      window.location.href = "https://learn.policyarthlabs.com/";
    }, 2200); // 2.2s waits for the doors to mostly open before navigating away
  };

  const scrollToNext = () => {
    document.getElementById('benefits')?.scrollIntoView({ behavior: 'smooth' });
  };

  const iconMap = {
    Building2,
    GraduationCap,
    Brain,
    Users
  };

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-red-50">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-20 -left-40 w-96 h-96 bg-gradient-to-br from-[#8B3A3A]/20 to-transparent rounded-full blur-3xl animate-pulse"
          style={{
            animationDuration: '4s',
            transform: `translate3d(${mousePos.x}px, ${mousePos.y}px, 0)`
          }}
        ></div>
        <div
          className="absolute bottom-20 -right-40 w-[600px] h-[600px] bg-gradient-to-tl from-[#4A5568]/20 to-transparent rounded-full blur-3xl animate-pulse"
          style={{
            animationDuration: '5s',
            animationDelay: '1s',
            transform: `translate3d(${-mousePos.x}px, ${-mousePos.y}px, 0)`
          }}
        ></div>

        {/* 3D Asset Decorations */}
        <div className="absolute top-[15%] left-[10%] opacity-10">
          <Cube3D color="#8B3A3A" size={60} />
        </div>
        <div className="absolute top-[25%] right-[12%] opacity-15">
          <Pyramid3D color="#4A5568" size={70} />
        </div>
        <div className="absolute bottom-[30%] left-[15%] opacity-12">
          <Sphere3D color="#8B3A3A" size={50} />
        </div>
        <div className="absolute top-[50%] right-[8%] opacity-10">
          <Hexagon3D color="#6B2A2A" size={80} />
        </div>
        <div className="absolute bottom-[20%] right-[20%] opacity-15">
          <Cube3D color="#4A5568" size={45} />
        </div>

        {/* Floating Particles with 3D effect */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-br from-[#8B3A3A] to-[#4A5568] rounded-full opacity-20 animate-float-3d"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`
            }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 flex-1 flex flex-col justify-center">
        {/* Logo Badge */}
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8B3A3A]/10 to-[#4A5568]/10 backdrop-blur-sm px-8 py-4 rounded-full border border-[#8B3A3A]/20 mb-8">
            <Sparkles className="w-5 h-5 text-[#8B3A3A]" />
            <span className="text-base font-bold text-slate-700">POLICY ARTH Labs</span>
          </div>
        </div>

        {/* Main Tagline - Single Line */}
        <div className={`text-center mb-8 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 tracking-tight leading-none">
            <span className="bg-gradient-to-r from-[#2D3748] via-[#4A5568] to-[#2D3748] bg-clip-text text-transparent">
              Learn. Connect. Lead.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-light">
            {heroData.description}
          </p>
        </div>

        {/* Three Product Cards */}
        <div className={`mt-16 mb-12 transition-all duration-1000 delay-400 perspective-container ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {featuresData.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              const isExpanded = activeCard === feature.id;
              const isCardVisible = visibleCards.includes(index);

              return (
                <Card
                  key={feature.id}
                  className={`group relative overflow-hidden bg-white/80 backdrop-blur-xl border-2 border-slate-200/50 hover:border-[#8B3A3A]/40 hover:shadow-2xl transition-all duration-300 ${isCardVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                    } hover:scale-[1.05]`}
                  style={{
                    transitionDelay: `${index * 150}ms`,
                    transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)'
                  }}
                >
                  <CardContent className="p-0 h-full flex flex-col" style={{ transformStyle: 'preserve-3d' }}>
                    {/* Compact View */}
                    <>
                      {/* Icon Header */}
                      <div className="p-10 pb-6">
                        <div className="relative mb-8 flex justify-center">
                          <IsometricContainer color={index === 0 ? "#8B3A3A" : index === 1 ? "#4A5568" : index === 2 ? "#6B2A2A" : "#2D8B57"}>
                            {IconComponent && <IconComponent className="w-10 h-10 text-white" />}
                          </IsometricContainer>
                        </div>

                        <div
                          className="inline-block px-4 py-2 bg-gradient-to-r from-[#8B3A3A]/10 to-[#6B2A2A]/10 rounded-full text-sm font-bold text-[#8B3A3A] mb-4"
                        >
                          {feature.subtitle}
                        </div>

                        <h3
                          className="text-2xl md:text-3xl font-black text-slate-900 mb-4 group-hover:text-[#8B3A3A] transition-colors duration-300"
                        >
                          {feature.title}
                        </h3>

                        <p
                          className="text-slate-600 text-base leading-relaxed line-clamp-4 mb-6"
                        >
                          {feature.description}
                        </p>
                      </div>

                      {/* Button */}
                      <div
                        className="p-10 pt-0 mt-auto"
                      >
                        {feature.id === 2 ? (
                          <Button
                            variant="outline"
                            onClick={handleAcademyRedirect}
                            className="w-full border-2 border-[#8B3A3A]/30 text-[#8B3A3A] hover:bg-[#8B3A3A] hover:text-white px-8 py-6 rounded-full font-bold text-base group-hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-[#8B3A3A]/20"
                          >
                            Learn More
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        ) : (
                          <Link to={`/products/${['advisor', 'academy', 'thinktank', 'community'][index]}`} className="block w-full">
                            <Button
                              variant="outline"
                              className="w-full border-2 border-[#8B3A3A]/30 text-[#8B3A3A] hover:bg-[#8B3A3A] hover:text-white px-8 py-6 rounded-full font-bold text-base group-hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-[#8B3A3A]/20"
                            >
                              Learn More
                              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className={`flex flex-col sm:flex-row gap-4 justify-center mb-12 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Button
            size="lg"
            variant="outline"
            className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 px-12 py-7 text-lg font-bold rounded-full hover:scale-105 transition-all duration-300"
          >
            {heroData.ctaSecondary}
          </Button>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={scrollToNext}
          className={`mx-auto transition-all duration-1000 delay-800 cursor-pointer group ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          aria-label="Scroll to next section"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-sm font-semibold text-slate-600">Discover More</span>
            <ChevronDown className="w-6 h-6 text-[#8B3A3A] animate-bounce group-hover:text-[#6B2A2A] transition-colors" />
          </div>
        </button>
      </div>

      {/* Academy Redirect Door Animation Overlay */}
      {isRedirecting && (
        <div className="fixed inset-0 z-[9999] pointer-events-none">
          {/* Left Door */}
          <div className="door-left absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-[#8B3A3A]/20 to-transparent opacity-50"></div>
            {/* Door Details */}
            <div className="absolute top-1/2 right-8 -translate-y-1/2 w-6 h-20 bg-gradient-to-r from-amber-600 to-amber-700 rounded-full shadow-lg door-handle"></div>
            <div className="absolute inset-4 border-4 border-slate-700/50 rounded-lg"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-[#8B3A3A]/10 to-transparent rounded-full blur-3xl"></div>
          </div>

          {/* Right Door */}
          <div className="door-right absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-slate-900 via-slate-800 to-slate-900 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-bl from-[#8B3A3A]/20 to-transparent opacity-50"></div>
            {/* Door Details */}
            <div className="absolute top-1/2 left-8 -translate-y-1/2 w-6 h-20 bg-gradient-to-l from-amber-600 to-amber-700 rounded-full shadow-lg door-handle"></div>
            <div className="absolute inset-4 border-4 border-slate-700/50 rounded-lg"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-bl from-[#8B3A3A]/10 to-transparent rounded-full blur-3xl"></div>
          </div>

          {/* Center Glow Effect */}
          <div className="door-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-full bg-gradient-to-b from-transparent via-white to-transparent opacity-0"></div>

          {/* Product Name & Brand Text on Doors */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center z-10 transition-opacity duration-1000">
            <h2
              className="text-4xl md:text-5xl font-black mb-4 tracking-tight drop-shadow-2xl"
              style={{ color: "#3B82F6" }}
            >
              Academy
            </h2>
            <p className="text-lg md:text-xl text-amber-400 font-bold tracking-wide drop-shadow-lg">
              By Policy Arth Labs
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .animate-fade-in-fast {
          animation: fadeIn 0.4s ease-out forwards;
          animation-delay: 0.2s;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default HeroSection;
